import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Facebook, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { createSocialLead } from '@/lib/api';

export default function SocialCapture() {
  const { platform } = useParams();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const platformName = platform === 'instagram' ? 'Instagram' : 'Facebook';
  const PlatformIcon = platform === 'instagram' ? Instagram : Facebook;
  const platformColor = platform === 'instagram' ? 'text-pink-500' : 'text-blue-500';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await createSocialLead({
        ...form,
        last_name: form.last_name || '',
        source: platform || 'social',
      });
      setSubmitted(true);
      toast.success('You\'re in! Check your email for next steps.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="social-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-rtb-purple/20 rounded-full flex items-center justify-center mx-auto mb-6 gold-glow">
            <CheckCircle2 className="w-10 h-10 text-rtb-purple" />
          </div>
          <h1 className="text-3xl font-oswald font-bold uppercase mb-4">
            You're <span className="text-rtb-purple">In!</span>
          </h1>
          <p className="text-white/60 mb-8">
            We've received your info. Our team will reach out shortly with exclusive details about the program.
          </p>
          <Button onClick={() => navigate('/qualify')} className="btn-primary" data-testid="social-qualify-btn">
            Complete Full Qualification <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4 py-12" data-testid="social-capture-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <CheckCircle2 className="w-6 h-6 text-rtb-purple" />
            <span className="font-oswald font-bold tracking-wider">READYTOBUILDIT.LIFE</span>
          </div>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 mb-6">
            <PlatformIcon className={`w-5 h-5 ${platformColor}`} />
            <span className="text-sm text-white/60">Exclusive {platformName} Offer</span>
          </div>

          <h1 className="text-3xl font-oswald font-bold uppercase mb-3">
            Get <span className="text-rtb-purple">Free Access</span> to Our<br />
            Next Live Strategy Session
          </h1>
          <p className="text-white/60 text-sm">
            Learn how to acquire land for as little as $500 and build generational wealth. 
            Limited spots available.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-panel p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/60 mb-1.5 block text-sm">First Name *</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="input-dark"
                placeholder="First name"
                required
                data-testid="social-first-name"
              />
            </div>
            <div>
              <Label className="text-white/60 mb-1.5 block text-sm">Last Name</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="input-dark"
                placeholder="Last name"
                data-testid="social-last-name"
              />
            </div>
          </div>

          <div>
            <Label className="text-white/60 mb-1.5 block text-sm">Email *</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input-dark"
              placeholder="you@example.com"
              required
              data-testid="social-email"
            />
          </div>

          <div>
            <Label className="text-white/60 mb-1.5 block text-sm">Phone *</Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="input-dark"
              placeholder="(555) 000-0000"
              required
              data-testid="social-phone"
            />
          </div>

          <Button type="submit" disabled={loading} className="btn-primary w-full" data-testid="social-submit-btn">
            {loading ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>Reserve My Spot <ArrowRight className="ml-2 w-5 h-5" /></>
            )}
          </Button>

          <p className="text-center text-white/30 text-xs">
            Your info is secure. We never share your data.
          </p>
        </form>
      </motion.div>
    </div>
  );
}
